/**
 * Kassierer-Login von der Login-Seite (Name + Code).
 */

import { offlineAuthService } from './offlineAuthService';
import {
  loadWorkersForBusiness,
  parseWorkersKvValue,
  resolveWorkersBusinessId
} from '../utils/workersStorage';
import { enrichCurrentBusinessFromOfflineStore } from '../utils/localManagerCode';
import { kvKeysStartingWith } from '../infrastructure/storage/kvAccess';
import { normalizeBusinessStorageId } from '../utils/sharedCore';
import sessionRecorder from '../utils/sessionRecorder';
import {
  credentialsMatch,
  ensureAuthStorageRehydrated,
  normalizeCredential,
  readKvEntriesWithPrefix,
  readOfflineBusinesses
} from '../utils/authCredentialRead';

function registerBusinessCandidate(byId, businessId, seed = {}) {
  if (!businessId) return;
  const normalized = normalizeBusinessStorageId(businessId);
  const fullId =
    seed?.id != null && String(seed.id).startsWith('business_')
      ? String(seed.id)
      : String(businessId).startsWith('business_')
        ? String(businessId)
        : `business_${normalized}`;
  const existing = byId.get(normalized) || byId.get(fullId) || {};
  const merged = { ...existing, ...seed, id: fullId };
  byId.set(normalized, merged);
  if (fullId !== normalized) {
    byId.set(fullId, merged);
  }
}

function registerBusinessesFromWorkerKeys(byId) {
  for (const key of kvKeysStartingWith('workers_')) {
    const suffix = key.slice('workers_'.length);
    if (!suffix) continue;
    registerBusinessCandidate(byId, suffix);
    if (!String(suffix).startsWith('business_')) {
      registerBusinessCandidate(byId, `business_${suffix}`);
    }
  }

  const legacyWorkers = loadWorkersForBusiness(null);
  if (legacyWorkers.workers.length > 0) {
    for (const business of offlineAuthService.getBusinesses()) {
      if (business?.id) registerBusinessCandidate(byId, business.id);
    }
  }
}

/**
 * Sucht einen Kassierer in allen workers_*-Keys (Fallback nach Logout ohne Session).
 */
export function findWorkerAcrossAllBusinessKeys(name, code) {
  const normalizedName = normalizeCredential(name).toLowerCase();
  const normalizedCode = normalizeCredential(code);
  if (!normalizedName || !normalizedCode) return null;

  const seenKeys = new Set();
  const allWorkerKeys = [
    ...kvKeysStartingWith('workers_'),
    'workers'
  ];

  for (const key of allWorkerKeys) {
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    const suffix = key === 'workers' ? null : key.slice('workers_'.length);
    const { workers } = loadWorkersForBusiness(suffix || null);
    const worker = workers.find(
      (w) =>
        normalizeCredential(w.name).toLowerCase() === normalizedName &&
        credentialsMatch(w.code, normalizedCode)
    );
    if (!worker) continue;
    if (worker.isActive === false) {
      return { errorCode: 'WORKER_DEACTIVATED' };
    }
    const businessId = suffix || resolveWorkersBusinessId(null);
    const businesses = offlineAuthService.getBusinesses();
    const fromList = businesses.find(
      (b) => normalizeBusinessStorageId(b.id) === normalizeBusinessStorageId(businessId)
    );
    const business =
      fromList ||
      enrichCurrentBusinessFromOfflineStore(businessId) || {
        id: businessId.startsWith('business_') ? businessId : `business_${businessId}`,
        business_name: 'Business',
        offline: true
      };
    return { worker, business };
  }

  return null;
}

async function loadWorkersFromAllStorageKeys() {
  const merged = new Map();

  const ingest = (key, raw) => {
    const { workers, corrupted } = parseWorkersKvValue(raw);
    if (corrupted || !Array.isArray(workers) || workers.length === 0) return;
    const suffix = key === 'workers' ? null : key.slice('workers_'.length);
    const businessId = suffix || resolveWorkersBusinessId(null);
    if (!merged.has(key)) {
      merged.set(key, { businessId, workers });
    }
  };

  for (const key of kvKeysStartingWith('workers_')) {
    const suffix = key.slice('workers_'.length);
    const { workers } = loadWorkersForBusiness(suffix || null);
    if (workers.length > 0) ingest(key, workers);
  }

  const directEntries = await readKvEntriesWithPrefix('workers_');
  for (const { key, value } of directEntries) {
    ingest(key, value);
  }

  const legacy = loadWorkersForBusiness(null);
  if (legacy.workers.length > 0) {
    ingest('workers', legacy.workers);
  }

  return [...merged.values()];
}

function matchWorkerInList(workers, name, code) {
  const normalizedName = normalizeCredential(name).toLowerCase();
  const normalizedCode = normalizeCredential(code);
  return workers.find(
    (w) =>
      normalizeCredential(w.name).toLowerCase() === normalizedName &&
      credentialsMatch(w.code, normalizedCode)
  );
}

async function collectBusinessCandidates() {
  await ensureAuthStorageRehydrated();

  const byId = new Map();
  const offlineList = await readOfflineBusinesses();

  offlineList.forEach((business) => {
    if (business?.id) registerBusinessCandidate(byId, business.id, business);
  });

  offlineAuthService.getBusinesses().forEach((business) => {
    if (business?.id) registerBusinessCandidate(byId, business.id, business);
  });

  registerBusinessesFromWorkerKeys(byId);

  try {
    const { listKnownBusinessIds } = await import('../infrastructure/storage/businessIdDiscovery');
    const known = await listKnownBusinessIds();
    for (const row of known) {
      const id = row?.businessId;
      if (!id) continue;
      const fromOffline = offlineAuthService.getBusinesses().find((b) => b.id === id);
      registerBusinessCandidate(byId, id, fromOffline || { business_name: row.label || 'Business' });
    }
  } catch (_) {
    /* optional */
  }

  const uniqueIds = new Set(
    [...byId.values()].map((business) => normalizeBusinessStorageId(business.id))
  );

  for (const id of uniqueIds) {
    const enriched = enrichCurrentBusinessFromOfflineStore(id);
    if (enriched) {
      registerBusinessCandidate(byId, id, enriched);
    }
  }

  const seen = new Set();
  return [...byId.values()].filter((business) => {
    const key = normalizeBusinessStorageId(business.id);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * @param {string} name
 * @param {string} code
 */
export async function loginWorkerByNameAndCode(name, code) {
  await ensureAuthStorageRehydrated();

  const normalizedName = normalizeCredential(name).toLowerCase();
  const normalizedCode = normalizeCredential(code);

  if (!normalizedName || !normalizedCode) {
    return { success: false, errorCode: 'NAME_AND_CODE_REQUIRED' };
  }

  const businesses = await collectBusinessCandidates();

  const tryMatch = (business) => {
    const { workers } = loadWorkersForBusiness(business.id);
    const worker = matchWorkerInList(workers, normalizedName, normalizedCode);
    if (!worker) return null;
    if (worker.isActive === false) {
      return { success: false, errorCode: 'WORKER_DEACTIVATED' };
    }
    const pauseStatus = sessionRecorder.isCashRegisterPaused(business.id);
    if (pauseStatus && String(pauseStatus.workerId) !== String(worker.id)) {
      return { success: false, errorCode: 'CASH_REGISTER_PAUSED' };
    }
    return { success: true, business, worker };
  };

  for (const business of businesses) {
    const result = tryMatch(business);
    if (result) return result;
  }

  const allWorkerBuckets = await loadWorkersFromAllStorageKeys();
  for (const bucket of allWorkerBuckets) {
    const worker = matchWorkerInList(bucket.workers, normalizedName, normalizedCode);
    if (!worker) continue;
    if (worker.isActive === false) {
      return { success: false, errorCode: 'WORKER_DEACTIVATED' };
    }

    const businessId = bucket.businessId;
    const businessesList = offlineAuthService.getBusinesses();
    const fromList = businessesList.find(
      (b) => normalizeBusinessStorageId(b.id) === normalizeBusinessStorageId(businessId)
    );
    const business =
      fromList ||
      enrichCurrentBusinessFromOfflineStore(businessId) ||
      businesses.find(
        (b) => normalizeBusinessStorageId(b.id) === normalizeBusinessStorageId(businessId)
      ) || {
        id: String(businessId).startsWith('business_')
          ? businessId
          : `business_${businessId}`,
        business_name: 'Business',
        offline: true
      };

    const pauseStatus = sessionRecorder.isCashRegisterPaused(business.id);
    if (pauseStatus && String(pauseStatus.workerId) !== String(worker.id)) {
      return { success: false, errorCode: 'CASH_REGISTER_PAUSED' };
    }
    return { success: true, business, worker };
  }

  const fallback = findWorkerAcrossAllBusinessKeys(normalizedName, normalizedCode);
  if (fallback?.errorCode) {
    return { success: false, errorCode: fallback.errorCode };
  }
  if (fallback?.worker && fallback?.business) {
    const pauseStatus = sessionRecorder.isCashRegisterPaused(fallback.business.id);
    if (pauseStatus && String(pauseStatus.workerId) !== String(fallback.worker.id)) {
      return { success: false, errorCode: 'CASH_REGISTER_PAUSED' };
    }
    return { success: true, business: fallback.business, worker: fallback.worker };
  }

  if (businesses.length === 0) {
    return { success: false, errorCode: 'NO_LOCAL_BUSINESS' };
  }

  return { success: false, errorCode: 'INVALID_WORKER_CREDENTIALS' };
}

export const PENDING_WORKER_CASH_LOGIN_KEY = 'pendingWorkerCashLogin';
